import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { CrewMember } from '../api/crew.ts';
import {
  addLeaveType,
  allLeaveTypesQuery,
  cancelLeave,
  decideLeave,
  leaveQuery,
  pendingLeaveQuery,
  REASONS,
  retireUnavailability,
  setTimeOffOverride,
  setTimeOffSettings,
  timeOffSettingsQuery,
  unavailabilityQuery,
  updateLeaveType,
  whenLabel,
  type ClassSetting,
  type CrewClassKey,
  type Leave,
} from '../api/time-off.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { TimeOffForm, type TimeOffKind } from './time-off.tsx';

// CB-REQ CRW-02 at v1.9106, crew part 4D-2b: a person's unavailability and leave on their crew
// record, who may enter which (by class, and their own override), the leave types, and Home's leave
// to approve. The words are the operator's: Unavailability and Leave, never one word over both.

const STATE: Record<Leave['state'], string> = {
  pending: 'Not yet approved',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};
const CLASS: Record<CrewClassKey, string> = {
  employee: 'Employees',
  contractor: 'Contractors',
  subcontractor: 'Subcontractors',
};

/** Today, as a drawn whole day, for a new entry from the crew record. */
const todayWindow = (today: string) => {
  const next = new Date(Date.parse(`${today}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
  return { start: `${today}T00:00`, end: `${next}T00:00` };
};

function useRefresh(crewMemberId?: string) {
  const queryClient = useQueryClient();
  return async () => {
    if (crewMemberId) await queryClient.invalidateQueries({ queryKey: ['crew', crewMemberId] });
    await queryClient.invalidateQueries({ queryKey: ['leave'] });
    await queryClient.invalidateQueries({ queryKey: ['roster'] });
  };
}

/** A person's unavailability and leave, under their tickets on the crew record. */
export function CrewTimeOff({
  member,
  mayManage,
  mayApprove,
  today,
}: {
  member: CrewMember;
  mayManage: boolean;
  mayApprove: boolean;
  today: string;
}) {
  const unavailability = useQuery(unavailabilityQuery(member.id));
  const leave = useQuery(leaveQuery(member.id));
  const [adding, setAdding] = useState<TimeOffKind | null>(null);
  const refresh = useRefresh(member.id);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2" aria-labelledby={`unavailability-${member.id}`}>
        <div className="flex flex-wrap items-center gap-3">
          <h3 id={`unavailability-${member.id}`} className="font-heading text-sm font-extrabold">
            Unavailability
          </h3>
          {mayManage && adding !== 'unavailable' ? (
            <Button variant="quiet" className="small-btn" onClick={() => setAdding('unavailable')}>
              + Unavailable
            </Button>
          ) : null}
        </div>
        {adding === 'unavailable' ? (
          <TimeOffForm
            kind="unavailable"
            crewMemberId={member.id}
            drawn={todayWindow(today)}
            onDone={() => setAdding(null)}
          />
        ) : null}
        {unavailability.data?.length === 0 ? <p className="text-sm text-muted">None.</p> : null}
        <ul className="flex flex-col gap-1 text-sm">
          {(unavailability.data ?? []).map((u) => (
            <li key={u.id} className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold">{whenLabel(u.when)}</span>
              <span>{REASONS[u.reason]}</span>
              {u.repeatsWeekly ? (
                <span className="tag">
                  Weekly
                  {u.repeatUntil
                    ? ` to ${whenLabel({ days: { from: u.repeatUntil, to: u.repeatUntil } })}`
                    : ''}
                </span>
              ) : null}
              {u.note ? <span className="text-ink-soft">{u.note}</span> : null}
              {u.ownEntry ? <span className="text-xs text-muted">Their own</span> : null}
              {mayManage ? (
                <Button
                  variant="quiet"
                  className="small-btn"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => retireUnavailability(u.id))}
                >
                  Remove<span className="sr-only"> {whenLabel(u.when)}</span>
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2" aria-labelledby={`leave-${member.id}`}>
        <div className="flex flex-wrap items-center gap-3">
          <h3 id={`leave-${member.id}`} className="font-heading text-sm font-extrabold">
            Leave
          </h3>
          {mayManage && adding !== 'leave' ? (
            <Button variant="quiet" className="small-btn" onClick={() => setAdding('leave')}>
              + Leave
            </Button>
          ) : null}
        </div>
        {adding === 'leave' ? (
          <TimeOffForm
            kind="leave"
            crewMemberId={member.id}
            drawn={todayWindow(today)}
            onDone={() => setAdding(null)}
          />
        ) : null}
        {leave.data?.length === 0 ? <p className="text-sm text-muted">None.</p> : null}
        <ul className="flex flex-col gap-1 text-sm">
          {(leave.data ?? []).map((l) => (
            <LeaveLine
              key={l.id}
              leave={l}
              showName={false}
              mayManage={mayManage}
              mayApprove={mayApprove}
              busy={act.isPending}
              onAct={(run) => act.mutate(run)}
            />
          ))}
        </ul>
      </section>

      {mayManage ? <Override member={member} /> : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </div>
  );
}

function LeaveLine({
  leave: l,
  showName,
  mayManage,
  mayApprove,
  busy,
  onAct,
}: {
  leave: Leave;
  showName: boolean;
  mayManage: boolean;
  mayApprove: boolean;
  busy: boolean;
  onAct: (run: () => Promise<unknown>) => void;
}) {
  const live = l.state === 'pending' || l.state === 'approved';
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      {showName ? <span className="font-semibold">{l.crewMember.name}</span> : null}
      <span className={showName ? '' : 'font-semibold'}>{whenLabel(l.when)}</span>
      <span>{l.type.name}</span>
      <span className={`tag ${l.state === 'pending' ? 'tag-warn' : ''}`}>{STATE[l.state]}</span>
      {l.note ? <span className="text-ink-soft">{l.note}</span> : null}
      {l.decisionNote ? <span className="text-ink-soft">“{l.decisionNote}”</span> : null}
      {l.state === 'pending' && mayApprove ? (
        <>
          <Button
            variant="quiet"
            className="small-btn"
            disabled={busy}
            onClick={() => onAct(() => decideLeave(l.id, 'approve'))}
          >
            Approve<span className="sr-only"> {l.crewMember.name}’s leave</span>
          </Button>
          <Button
            variant="quiet"
            className="small-btn"
            disabled={busy}
            onClick={() => onAct(() => decideLeave(l.id, 'decline'))}
          >
            Decline<span className="sr-only"> {l.crewMember.name}’s leave</span>
          </Button>
        </>
      ) : null}
      {live && mayManage ? (
        <Button
          variant="quiet"
          className="small-btn"
          disabled={busy}
          onClick={() => onAct(() => cancelLeave(l.id))}
        >
          Cancel<span className="sr-only"> {l.crewMember.name}’s leave</span>
        </Button>
      ) : null}
    </li>
  );
}

const OVERRIDE = { class: 'As their class', yes: 'Yes', no: 'No' } as const;
type OverrideChoice = keyof typeof OVERRIDE;
const choiceOf = (v: boolean | null | undefined): OverrideChoice =>
  v === true ? 'yes' : v === false ? 'no' : 'class';
const valueOf = (c: OverrideChoice) => (c === 'yes' ? true : c === 'no' ? false : null);

/** May this person enter each for themselves: their class's setting, or their own. */
function Override({ member }: { member: CrewMember }) {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (o: { mayMarkUnavailable?: boolean | null; mayRequestLeave?: boolean | null }) =>
      setTimeOffOverride(member.id, o),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crew'] }),
  });
  const row = (label: string, key: 'mayMarkUnavailable' | 'mayRequestLeave') => (
    <label className="flex flex-wrap items-center gap-2 text-sm">
      <span className="min-w-48">{label}</span>
      <select
        className="input w-auto"
        value={choiceOf(member[key])}
        disabled={save.isPending}
        onChange={(e) => save.mutate({ [key]: valueOf(e.currentTarget.value as OverrideChoice) })}
      >
        {(Object.keys(OVERRIDE) as OverrideChoice[]).map((c) => (
          <option key={c} value={c}>
            {OVERRIDE[c]}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="font-heading text-sm font-extrabold">On their phone</legend>
      {row('Marks themselves unavailable', 'mayMarkUnavailable')}
      {row('Asks for leave', 'mayRequestLeave')}
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
    </fieldset>
  );
}

/** Settings: who may enter each on their phone, by class. */
export function TimeOffSettings({ editable }: { editable: boolean }) {
  const queryClient = useQueryClient();
  const settings = useQuery(timeOffSettingsQuery);
  const save = useMutation({
    mutationFn: (next: ClassSetting[]) => setTimeOffSettings(next),
    onSuccess: (data) => queryClient.setQueryData(timeOffSettingsQuery.queryKey, data),
  });
  const set = (
    cls: ClassSetting['class'],
    key: 'mayMarkUnavailable' | 'mayRequestLeave',
    v: boolean,
  ) => save.mutate((settings.data ?? []).map((s) => (s.class === cls ? { ...s, [key]: v } : s)));
  return (
    <section className="flex flex-col gap-3" aria-labelledby="time-off-settings">
      <h2 id="time-off-settings" className="section-title">
        Unavailability and leave on the phone
      </h2>
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">Who may enter each for themselves, by class</caption>
          <thead>
            <tr>
              <th scope="col">Class</th>
              <th scope="col">Unavailable</th>
              <th scope="col">Leave</th>
            </tr>
          </thead>
          <tbody>
            {(settings.data ?? []).map((s) => (
              <tr key={s.class}>
                <th scope="row">{CLASS[s.class as CrewClassKey]}</th>
                {(['mayMarkUnavailable', 'mayRequestLeave'] as const).map((key) => (
                  <td key={key}>
                    <input
                      type="checkbox"
                      aria-label={`${CLASS[s.class as CrewClassKey]}: ${key === 'mayMarkUnavailable' ? 'unavailable' : 'leave'}`}
                      checked={s[key]}
                      disabled={!editable || save.isPending}
                      onChange={(e) => set(s.class, key, e.currentTarget.checked)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
      <LeaveTypes editable={editable} />
    </section>
  );
}

/** Settings: the tenant's leave types, each paid or not, needing approval or not. */
function LeaveTypes({ editable }: { editable: boolean }) {
  const queryClient = useQueryClient();
  const types = useQuery(allLeaveTypesQuery);
  const [name, setName] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['leave-types'] });
  const change = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
  });
  function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    change.mutate(async () => {
      await addLeaveType({ name: name.trim(), paid: true, needsApproval: true });
      setName('');
    });
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-heading text-sm font-extrabold">Leave types</h3>
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">Leave types</caption>
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Paid</th>
              <th scope="col">Needs approval</th>
              {editable ? (
                <th scope="col" className="sr-only">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {(types.data ?? []).map((t) => (
              <tr key={t.id} className={t.retired ? 'text-muted' : undefined}>
                <th scope="row">
                  {t.name}
                  {t.retired ? <span className="tag ml-2">Retired</span> : null}
                </th>
                {(['paid', 'needsApproval'] as const).map((key) => (
                  <td key={key}>
                    <input
                      type="checkbox"
                      aria-label={`${t.name}: ${key === 'paid' ? 'paid' : 'needs approval'}`}
                      checked={t[key]}
                      disabled={!editable || t.retired || change.isPending}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        change.mutate(() => updateLeaveType(t.id, { [key]: checked }));
                      }}
                    />
                  </td>
                ))}
                {editable ? (
                  <td>
                    <Button
                      variant="quiet"
                      className="small-btn"
                      disabled={change.isPending}
                      onClick={() =>
                        change.mutate(() => updateLeaveType(t.id, { retired: !t.retired }))
                      }
                    >
                      {t.retired ? 'Restore' : 'Retire'}
                      <span className="sr-only"> {t.name}</span>
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
          <Field
            label="New leave type"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Button type="submit" variant="quiet" disabled={change.isPending || !name.trim()}>
            Add
          </Button>
        </form>
      ) : null}
      {change.isError ? (
        <p role="alert" className="alert">
          {change.error.message}
        </p>
      ) : null}
    </div>
  );
}

/** Home: leave asked for and not yet decided, with Approve and Decline in place. */
export function LeaveToApprove() {
  const pending = useQuery(pendingLeaveQuery);
  const refresh = useRefresh();
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
  });
  if (!pending.data || pending.data.length === 0) return null;
  return (
    <section aria-labelledby="leave-to-approve" className="flex flex-col gap-3">
      <h2 id="leave-to-approve" className="section-title">
        Leave to approve
      </h2>
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2 text-sm">
        {pending.data.map((l) => (
          <LeaveLine
            key={l.id}
            leave={l}
            showName
            mayManage={false}
            mayApprove
            busy={act.isPending}
            onAct={(run) => act.mutate(run)}
          />
        ))}
      </ul>
    </section>
  );
}
